import React from 'react';
import StatTile from '../StatTile';
import { formatNumber } from '../charts';
import { Badge, CopyButton } from '../ui';
import DatabaseCharts from './DatabaseCharts';
import { fmt } from './units';

export default function DatabasePanel({ data, range, loading }) {
  const a = data?.aurora || {};
  const c = data?.current || {};
  const connPct = c.max_connections ? (c.connections / c.max_connections) * 100 : null;
  const ms = fmt('ms').value;
  // Leave out what the environment does not run. Until the first answer, assume both.
  const hasProxy = !data || !!data.proxy;
  const hasReader = !data || (a.instances || []).some(i => i.role === 'reader');
  // A writer on a fixed instance class (no Serverless v2 scaling) has no ACU: the tile shows
  // its class instead. `serverless` is absent in an older answer, which meant Serverless.
  const scales = a.serverless !== false;
  return (
    <>
      <div className="dashboard-grid">
        {scales ? (
          <StatTile span={3} icon="database" label="Writer capacity" value={c.acu_writer != null ? `${c.acu_writer} ACU` : '—'}
            sub={`scales ${a.min_acu ?? '—'} to ${a.max_acu ?? '—'} ACU`}
            tone={a.max_acu && c.acu_writer >= a.max_acu * 0.9 ? 'warning' : undefined} />
        ) : (
          <StatTile span={3} icon="database" label="Writer instance" value={a.writer_class || '—'}
            sub={c.cpu_writer != null ? `fixed size · CPU ${Math.round(c.cpu_writer)}%` : 'fixed size, no ACU scaling'}
            tone={c.cpu_writer >= 80 ? 'warning' : undefined} />
        )}
        <StatTile span={3} icon="activity" label="Connections" value={formatNumber(c.connections || 0)}
          sub={connPct == null ? '—' : `${Math.round(connPct)}% of max_connections`}
          tone={connPct >= 80 ? 'negative' : connPct >= 60 ? 'warning' : undefined}
          help={hasProxy
            ? "The ceiling that matters for the proxy fleet. proxy-llm, proxy-channels, proxy-tools, proxy-crons, proxy-web and the bridges connect to Aurora directly rather than through the RDS Proxy, so every replica added spends from this pool."
            : "The ceiling that matters for every service: they all connect to Aurora directly, so every replica added spends from this pool."} />
        <StatTile span={3} icon="clock" label="Commit latency" value={c.commit_latency_ms != null ? ms(c.commit_latency_ms) : '—'}
          sub="writer, average" />
        {hasReader ? (
          <StatTile span={3} icon="layers" label="Replica lag" value={c.replica_lag_ms != null ? ms(c.replica_lag_ms) : '—'}
            sub="reader behind writer" tone={c.replica_lag_ms > 1000 ? 'warning' : undefined} />
        ) : (
          <StatTile span={3} icon="layers" label="Instances" value={formatNumber((a.instances || []).length)}
            sub="writer only, no reader" />
        )}
      </div>

      <div className="dashboard-grid">
        <div className={`card dashboard-card ${hasProxy ? 'dashboard-tile-6' : 'dashboard-tile-12'}`}>
          <div className="tile-head">
            <div className="tile-head-text">
              <h3 className="tile-title">Aurora cluster</h3>
              <div className="tile-subtitle">{a.engine || '—'}</div>
            </div>
          </div>
          <dl className="kv">
            <dt>Cluster</dt>
            <dd><span className="row gap-1"><span className="mono text-sm">{a.cluster || '—'}</span>
              {a.cluster && <CopyButton value={a.cluster} label="Copy cluster name" />}</span></dd>
            {scales ? (
              <>
                <dt>Capacity range</dt>
                <dd>{a.min_acu ?? '—'} to {a.max_acu ?? '—'} ACU · 1 ACU is about 2 GiB of memory</dd>
              </>
            ) : (
              <>
                <dt>Instance class</dt>
                <dd><span className="mono text-sm">{a.writer_class || '—'}</span> · fixed size, no Serverless scaling</dd>
              </>
            )}
            {(a.instances || []).map(i => (
              <React.Fragment key={i.id}>
                <dt className="row gap-2"><Badge tone={i.role === 'writer' ? 'accent' : 'neutral'}>{i.role}</Badge></dt>
                <dd><span className="mono text-sm">{i.id}</span> <span className="text-muted">· {i.zone}{i.instance_class ? ` · ${i.instance_class}` : ''}</span></dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
        {hasProxy && (
        <div className="card dashboard-card dashboard-tile-6">
          <div className="tile-head">
            <div className="tile-head-text">
              <h3 className="tile-title">RDS Proxy</h3>
              <div className="tile-subtitle">used by the backend, admin console and control plane</div>
            </div>
          </div>
          <dl className="kv">
            <dt>Proxy</dt>
            <dd><span className="mono text-sm">{data?.proxy?.name || '—'}</span></dd>
            <dt>Authenticates to Aurora with</dt>
            <dd>{data?.proxy?.auth || '—'}</dd>
            <dt>Client connections</dt>
            <dd className="num">{formatNumber(c.proxy_client_connections || 0)}</dd>
            <dt>Database connections</dt>
            <dd className="num">{formatNumber(c.proxy_db_connections || 0)}
              <span className="text-muted"> · pooled from the clients above</span></dd>
          </dl>
        </div>
        )}
      </div>

      <div className="section-head">
        <h2>Over time</h2>
      </div>
      {/* Shared with the load tests' report, which draws them over a run's window. */}
      <DatabaseCharts data={data} range={range} loading={loading} />
    </>
  );
}
